import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {z as zod} from "zod";
import {uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";

export enum SchemaPropertyType {
    boolean = "boolean",
    integer = "integer",
    json = "json",
    number = "number",
    string = "string",
    timestamp = "timestamp",
    uuid = "uuid",
}

export type SchemaProperty = {
    defaultValue?: boolean | number | string | null;
    displayName: string;
    id: Uuid;
    internalName: string;
    required?: boolean;
    type: SchemaPropertyType;
    unique?: boolean;
};

const schemaPropertyValidator: zod.ZodType<SchemaProperty> = zod.object({
    defaultValue: zod.union([
        zod.string(),
        zod.number(),
        zod.boolean(),
    ]).nullable().optional(),
    displayName: zod.string(),
    id: uuidZodValidator,
    internalName: zod.string().regex(/^[a-zA-Z]+$/, "Internal name can only be letters."),
    required: zod.boolean().optional(),
    type: zod.enum(SchemaPropertyType),
    unique: zod.boolean().optional(),
});

const schemaPropertyNameValidator = zod.string().refine(
    (value) => /^[a-z_]+$/.test(value) || uuidZodValidator.safeParse(value).success,
    "Property keys must be lowercase letters and underscores or a UUID",
);

export const storageDatabaseSchemaValidator: zod.ZodType<StorageDatabaseSchema> = zod.object({
    properties: zod.record(
        schemaPropertyNameValidator,
        schemaPropertyValidator,
    ),
});

export type StorageDatabaseSchema = {
    properties: Record<string, SchemaProperty>;
};

export const SQL_TYPE_MAP: Record<string, string> = {
    boolean: "boolean",
    integer: "integer",
    json: "jsonb",
    number: "numeric",
    string: "text",
    timestamp: "timestamptz",
    uuid: "uuid",
};

export function formatSqlDefaultValue(value: boolean | number | string): string {
    if (typeof value === "string") {
        return `'${value.replace(/'/g, "''")}'`;
    }
    return String(value);
}

export function quoteSqlIdentifier(identifier: string): string {
    return `"${identifier.replace(/"/g, "\"\"")}"`;
}

function buildColumnDef({
    colName,
    includeUniqueConstraints,
    prop,
    tableName,
}: {
    colName: string;
    includeUniqueConstraints: boolean;
    prop: Omit<SchemaProperty, "id">;
    tableName: string;
}): string {
    const sqlType = SQL_TYPE_MAP[prop.type] ?? "text";
    const nullability = prop.required === true ? "NOT NULL" : "NULL";
    const defaultClause =
        prop.defaultValue !== undefined && prop.defaultValue !== null
            ? ` DEFAULT ${formatSqlDefaultValue(prop.defaultValue)}`
            : "";
    const uniqueConstraint = includeUniqueConstraints && prop.unique === true
        ? ` CONSTRAINT ${quoteSqlIdentifier(`uq_${tableName}_${colName}`)} UNIQUE`
        : "";
    return `    ${quoteSqlIdentifier(colName)} ${sqlType} ${nullability}${defaultClause}${uniqueConstraint}`;
}

export type BuildTableSqlResult = {
    completeSchema: StorageDatabaseSchema;
    createQuery: string;
};

// TODO: Check if we can use drizzle to build this
export function buildTableSql({
    includeUniqueConstraints = true,
    primaryKeyColumnName = "id",
    schema,
    tableName,
    templateId,
    workspaceId,
}: {
    includeUniqueConstraints?: boolean;
    primaryKeyColumnName?: string;
    schema: StorageDatabaseSchema;
    tableName?: string;
    templateId: Uuid;
    workspaceId: Uuid;
}): BuildTableSqlResult {
    const targetTableName = tableName ?? templateId;
    const columnDefs: Array<string> = [];
    for (const [colName, prop] of Object.entries(schema.properties)) {
        columnDefs.push(buildColumnDef({
            colName: colName,
            includeUniqueConstraints: includeUniqueConstraints,
            prop: prop,
            tableName: targetTableName,
        }));
    }

    const createQuery = [
        `CREATE TABLE ${quoteSqlIdentifier(workspaceId)}.${quoteSqlIdentifier(targetTableName)} (`,
        columnDefs.join(",\n"),
        `,`,
        `    CONSTRAINT ${quoteSqlIdentifier(`${targetTableName}_pk`)} PRIMARY KEY (${quoteSqlIdentifier(primaryKeyColumnName)})`,
        `);`,
    ].join("\n");

    return {
        completeSchema: schema,
        createQuery: createQuery,
    };
}
